import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Multi-select filters over the LLM cost dimensions (model display name,
 * trigger, agent type + node pair), shared by the Daily Cost chart and the
 * Model Breakdown table. Selections are sent to the platform-stats endpoints
 * as one repeated query param per kept value (`trigger=a&trigger=b`).
 */

export type CostDimension = 'model' | 'trigger' | 'agent';

export const COST_DIMENSIONS: CostDimension[] = ['model', 'trigger', 'agent'];

export const DIMENSION_LABELS: Record<CostDimension, string> = {
  model: 'Model',
  trigger: 'Trigger',
  agent: 'Agent type / node',
};

const DIMENSION_PLURALS: Record<CostDimension, string> = {
  model: 'models',
  trigger: 'triggers',
  agent: 'agents',
};

/**
 * Values to keep per dimension. A missing key means "all"; an empty array
 * means the user deselected everything (nothing to show, no request made).
 */
export type CostDimensionFilters = Partial<Record<CostDimension, string[]>>;

/** `dim=value` query fragments for every kept value, in dimension order. */
export function dimensionFilterParams(
  filters: CostDimensionFilters,
  dimensions: CostDimension[] = COST_DIMENSIONS,
): string[] {
  const params: string[] = [];
  for (const dim of dimensions) {
    for (const value of filters[dim] ?? []) {
      params.push(`${dim}=${encodeURIComponent(value)}`);
    }
  }
  return params;
}

/** True when some dimension has every value deselected. */
export function hasEmptySelection(
  filters: CostDimensionFilters,
  dimensions: CostDimension[] = COST_DIMENSIONS,
): boolean {
  return dimensions.some((dim) => filters[dim]?.length === 0);
}

interface FilterDropdownProps {
  dimension: CostDimension;
  /** Values seen in the current window (from the last response). */
  options: string[];
  /** Current selection; `undefined` = all. */
  selected: string[] | undefined;
  onChange: (selected: string[] | undefined) => void;
}

/** Checkbox multi-select with Select all / Clear all. */
export const FilterDropdown: FC<FilterDropdownProps> = ({
  dimension,
  options,
  selected,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Keep selected values that dropped out of the window's options visible so
  // they can still be unticked.
  const choices = useMemo(() => {
    const extra = (selected ?? []).filter((v) => !options.includes(v));
    return [...extra, ...options];
  }, [options, selected]);

  const label = DIMENSION_LABELS[dimension];
  const plural = DIMENSION_PLURALS[dimension];
  const isAll = selected === undefined;
  const selectedSet = new Set(selected ?? []);
  const summary = isAll
    ? `All ${plural}`
    : selected.length === 0
      ? `No ${plural}`
      : selected.length === 1
        ? selected[0]
        : `${selected.length} of ${choices.length} ${plural}`;

  const toggle = (value: string) => {
    const base = isAll ? choices : selected;
    const next = base.includes(value)
      ? base.filter((v) => v !== value)
      : [...base, value];
    // Ticking the last unticked box is the same as "all".
    onChange(next.length === choices.length ? undefined : next);
  };

  return (
    <div className={`cost-filter${isAll ? '' : ' filtered'}`} ref={rootRef}>
      <button
        type='button'
        className='cost-filter-toggle'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={`Filter by ${label.toLowerCase()}`}
        title={`${label}: ${summary}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className='filter-dim'>{label}</span>
        <span className='filter-summary'>{summary}</span>
        <span className='filter-caret' aria-hidden='true'>
          ▾
        </span>
      </button>
      {open && (
        <div className='cost-filter-menu' role='listbox' aria-multiselectable>
          <div className='filter-menu-actions'>
            <button
              type='button'
              disabled={isAll}
              onClick={() => onChange(undefined)}
            >
              Select all
            </button>
            <button
              type='button'
              disabled={!isAll && selected.length === 0}
              onClick={() => onChange([])}
            >
              Clear all
            </button>
          </div>
          {choices.length === 0 ? (
            <div className='filter-menu-empty'>No {plural} in this range</div>
          ) : (
            choices.map((value) => {
              const checked = isAll || selectedSet.has(value);
              return (
                <label
                  key={value}
                  className='filter-menu-option'
                  role='option'
                  aria-selected={checked}
                >
                  <input
                    type='checkbox'
                    checked={checked}
                    onChange={() => toggle(value)}
                  />
                  <span title={value}>{value}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

interface CostFilterBarProps {
  dimensions: CostDimension[];
  /** Per-dimension values seen in the current window. */
  options: Partial<Record<CostDimension, string[]>> | undefined;
  filters: CostDimensionFilters;
  onFiltersChange: (filters: CostDimensionFilters) => void;
}

/** One dropdown per dimension plus a "Clear filters" reset. */
export const CostFilterBar: FC<CostFilterBarProps> = ({
  dimensions,
  options,
  filters,
  onFiltersChange,
}) => {
  const activeFilters = dimensions.filter((dim) => filters[dim] !== undefined);
  return (
    <div className='cost-filters'>
      <span className='control-label'>Filter</span>
      {dimensions.map((dim) => (
        <FilterDropdown
          key={dim}
          dimension={dim}
          options={options?.[dim] ?? []}
          selected={filters[dim]}
          onChange={(selected) => {
            const next = { ...filters };
            if (selected === undefined) delete next[dim];
            else next[dim] = selected;
            onFiltersChange(next);
          }}
        />
      ))}
      {activeFilters.length > 0 && (
        <button
          type='button'
          className='clear-filters-btn'
          onClick={() => onFiltersChange({})}
        >
          Clear filters
        </button>
      )}
    </div>
  );
};
